import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/no-auth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { IsAuthorizedGuard } from './core/auth/guards/is-authorized.guard';
import { FeatureCodes } from './shared/enums/feature-codes';

// @formatter:off
// tslint:disable:max-line-length
export const appRoutes: Route[] = [
  // Redirect empty path to '/example'
  {
    path: '',
    pathMatch: 'full',
    redirectTo: '/home/profile',
  },

  // Redirect signed in user to the '/example'
  //
  // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  {
    path: 'signed-in-redirect',
    pathMatch: 'full',
    redirectTo: '/home/profile',
  },

  // Auth routes for guests
  {
    path: '',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'forgot-password',
        loadChildren: () =>
          import('app/modules/auth/forgot-password/forgot-password.module').then(
            (m) => m.AuthForgotPasswordModule,
          ),
      },
      {
        path: 'reset-password',
        loadChildren: () =>
          import('app/modules/auth/reset-password/reset-password.module').then(
            (m) => m.AuthResetPasswordModule,
          ),
      },
      {
        path: 'sign-in',
        loadChildren: () =>
          import('app/modules/auth/sign-in/sign-in.module').then((m) => m.AuthSignInModule),
      },
      {
        path: 'sign-up',
        loadChildren: () =>
          import('app/modules/auth/sign-up/sign-up.module').then((m) => m.AuthSignUpModule),
      },
    ],
  },

  // Auth routes for authenticated users
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'sign-out',
        loadChildren: () =>
          import('app/modules/auth/sign-out/sign-out.module').then((m) => m.AuthSignOutModule),
      },
      {
        path: 'unlock-session',
        loadChildren: () =>
          import('app/modules/auth/unlock-session/unlock-session.module').then(
            (m) => m.AuthUnlockSessionModule,
          ),
      },
    ],
  },

  // Admin routes
  {
    path: '',
    component: LayoutComponent,
    children: [
      // home
      {
        path: 'home',
        data: {
          breadcrumb: {
            label: 'Home',
            info: { myData: { icon: 'home', iconType: 'material' } },
          },
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'profile',
          },
          {
            canActivate: [IsAuthorizedGuard],
            canActivateChild: [IsAuthorizedGuard],
            data: {
              breadcrumb: 'Company',
              feature: FeatureCodes.company,
            },
            path: 'company',
            loadChildren: () =>
              import('app/modules/admin/apps/companies/companies.module').then(
                (m) => m.CompaniesModule,
              ),
          },
          {
            canActivate: [IsAuthorizedGuard],
            canActivateChild: [IsAuthorizedGuard],
            path: 'features',
            data: {
              breadcrumb: 'Features',
              feature: FeatureCodes.features,
            },
            loadChildren: () =>
              import('app/modules/admin/apps/features/features.module').then(
                (m) => m.FeaturesModule,
              ),
          },
          {
            canActivate: [IsAuthorizedGuard],
            canActivateChild: [IsAuthorizedGuard],
            path: 'groups',
            data: {
              breadcrumb: 'Groups',
              feature: FeatureCodes.groups,
            },
            loadChildren: () =>
              import('app/modules/admin/apps/groups/groups.module').then((m) => m.GroupsModule),
          },
          {
            canActivate: [IsAuthorizedGuard],
            canActivateChild: [IsAuthorizedGuard],
            path: 'userfeatures',
            data: {
              breadcrumb: 'User features',
              feature: FeatureCodes.userFeatures,
            },
            loadChildren: () =>
              import('app/modules/admin/apps/user-features/user-features.module').then(
                (m) => m.UserFeaturesModule,
              ),
          },
          {
            canActivate: [IsAuthorizedGuard],
            canActivateChild: [IsAuthorizedGuard],
            path: 'users',
            data: {
              breadcrumb: 'Users',
              feature: FeatureCodes.users,
            },
            loadChildren: () =>
              import('app/modules/admin/apps/users/users.module').then((m) => m.UsersModule),
          },
          {
            path: 'profile',
            canActivate: [IsAuthorizedGuard],
            canActivateChild: [IsAuthorizedGuard],
            data: {
              breadcrumb: 'Profile',
              feature: FeatureCodes.profile,
            },
            loadChildren: () =>
              import('app/modules/admin/pages/settings/settings.module').then(
                (m) => m.SettingsModule,
              ),
          },
          {
            canActivate: [IsAuthorizedGuard],
            canActivateChild: [IsAuthorizedGuard],
            path: 'paramprojects',
            data: {
              breadcrumb: 'ParamProjects',
              feature: FeatureCodes.paramProject,
            },
            loadChildren: () =>
              import('app/modules/admin/apps/paramProjects/paramProjects.module').then(
                (m) => m.ParamProjectsModule,
              ),
          },
          {
            canActivate: [IsAuthorizedGuard],
            canActivateChild: [IsAuthorizedGuard],
            path: 'projects',
            data: {
              breadcrumb: 'Projects',
              feature: FeatureCodes.projects,
            },
            loadChildren: () =>
              import('app/modules/admin/apps/projects/projects.module').then(
                (m) => m.ProjectsModule,
              ),
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: '/home/profile' },
];
